import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { CheckCircle, WarningCircle, XCircle, ShieldCheck } from "@phosphor-icons/react";

const CFG = {
  allowed: { cls: "border-emerald-500 bg-emerald-50", dot: "text-emerald-600", Icon: CheckCircle, label: "Allowed" },
  warning: { cls: "border-amber-500 bg-amber-50", dot: "text-amber-600", Icon: WarningCircle, label: "Warning / Approval" },
  not_allowed: { cls: "border-red-500 bg-red-50", dot: "text-red-600", Icon: XCircle, label: "Not Allowed" },
};

export default function EligibilityPanel({ assetId }) {
  const { hasPerm } = useAuth();
  const canOverride = hasPerm("supervisor", "administrator");
  const [data, setData] = useState(null);
  const [ov, setOv] = useState(null); // {profile_id, name}
  const [reason, setReason] = useState("");
  const [expires, setExpires] = useState("");

  const load = useCallback(() => { api.get(`/eligibility/asset/${assetId}`).then((r) => setData(r.data)).catch(() => {}); }, [assetId]);
  useEffect(() => { load(); }, [load]);

  const grantOverride = async () => {
    try {
      await api.post("/eligibility/overrides", { profile_id: ov.profile_id, asset_id: assetId, reason, expires_at: expires || null });
      toast.success("Override granted"); setOv(null); setReason(""); setExpires(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <p className="text-slate-400 text-sm p-2">Loading…</p>;
  const verb = data.verb === "drive" ? "Drive" : "Operate";

  return (
    <div className="flex flex-col gap-2" data-testid="eligibility-panel">
      <h3 className="font-display text-lg font-black text-slate-900">Who Can {verb} This?</h3>
      {data.results.map((r) => {
        const c = CFG[r.status];
        return (
          <div key={r.profile_id} data-testid={`elig-${r.profile_id}`} className={`rounded-lg border-l-4 border ${c.cls} p-3`}>
            <div className="flex items-center gap-2 flex-wrap">
              <c.Icon size={18} weight="fill" className={c.dot} />
              <span className="font-body font-semibold text-slate-900">{r.name}</span>
              <span className={`text-xs font-bold uppercase ${c.dot}`}>{c.label}</span>
              {r.overridden && <span className="text-[10px] font-bold uppercase bg-amber-200 text-amber-900 rounded px-1.5 py-0.5">Override</span>}
              {canOverride && r.status !== "allowed" && !r.overridden && (
                <button data-testid={`override-${r.profile_id}`} onClick={() => setOv(r)} className="ml-auto text-xs font-semibold text-blue-600 inline-flex items-center gap-1"><ShieldCheck size={14} weight="bold" />Override</button>
              )}
            </div>
            <ul className="mt-1 ml-6 flex flex-col gap-0.5">
              {r.reasons.map((x, i) => (
                <li key={i} className={`text-xs font-body ${x.ok ? "text-slate-500" : x.level === "not_allowed" ? "text-red-700" : "text-amber-700"}`}>
                  {x.ok ? "✓ " : "• "}{x.text}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {data.results.length === 0 && <p className="text-slate-400 text-sm">No active employees.</p>}

      <Dialog open={!!ov} onOpenChange={(o) => !o && setOv(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Manager Override — {ov?.name}</DialogTitle></DialogHeader>
          <p className="font-body text-sm text-slate-500">This authorises {ov?.name} despite failing eligibility. It is recorded in the audit trail.</p>
          <Textarea data-testid="override-reason" placeholder="Reason for override *" value={reason} onChange={(e) => setReason(e.target.value)} />
          <label className="text-xs font-semibold text-slate-600">Expiry (optional)</label>
          <Input data-testid="override-expiry" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          <Button data-testid="override-grant" onClick={grantOverride} disabled={!reason} className="bg-amber-600 text-white font-semibold">Grant Override</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
